// IndexedDB persistence for UI preferences.
// Replaces localStorage for hook-based preferences to avoid the shared ~5 MB quota.

import { CAP_TABLE_ROW_GROUPS_PREFERENCES_KEY } from 'common/constants/cap-table';
import {
  PINNED_COMPANIES_KEY,
  PINNED_FIRMS_KEY,
  PINNED_FUND_INVESTMENTS_KEY,
  PINNED_FUNDS_KEY,
  preferenceKeyForUser,
  SIDEBAR_ACTIVE_SECTION_KEY,
} from 'common/constants/userPreferences';
import { Logger } from 'utilities';
import { createIndexedDBStorageManager } from './indexedDBStorageManager';
import {
  PREFERENCES_BROADCAST_CHANNEL,
  PREFERENCES_DB_NAME,
  PREFERENCES_DB_VERSION,
  PREFERENCES_STORE_NAME,
} from './preferencesIndexedDB.constants';
import type { PreferenceChangeMessage } from './preferencesIndexedDB.types';
import {
  getPreferenceFallbackKey,
  isFlatCapTableRowGroupPrefs,
  isNestedCapTableRowGroupPrefs,
  isNumericKey,
  isPreferenceQuotaError,
} from './preferencesIndexedDB.utils';

const preferencesStorage = createIndexedDBStorageManager({
  dbName: PREFERENCES_DB_NAME,
  dbVersion: PREFERENCES_DB_VERSION,
  storeName: PREFERENCES_STORE_NAME,
  fallbackStorageKey: getPreferenceFallbackKey,
  logNamespace: 'Preferences storage',
});

export const isQuotaError = isPreferenceQuotaError;

const parseLegacyPreference = (raw: string): unknown => {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    // A few legacy consumers stored primitive strings without JSON encoding.
    return raw;
  }
};

export const getPreference = async <T,>(key: string): Promise<T | undefined> =>
  preferencesStorage.get<T>(key);

export const broadcastPreferenceChange = (key: string, value: unknown): void => {
  if (typeof window === 'undefined') {
    return;
  }
  // CustomEvent updates subscribers in the current tab; BroadcastChannel keeps
  // other open tabs in sync without requiring a refresh.
  window.dispatchEvent(
    new CustomEvent('localStorageChange', {
      detail: { key, value },
    })
  );
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(PREFERENCES_BROADCAST_CHANNEL);
      channel.postMessage({ key, value } satisfies PreferenceChangeMessage);
      channel.close();
    }
  } catch (error) {
    Logger.debug('Preferences broadcast failed:', error);
  }
};

export const setPreference = async (key: string, value: unknown): Promise<void> => {
  await preferencesStorage.set(key, value);
  broadcastPreferenceChange(key, value);
};

export const removePreference = async (key: string): Promise<void> => {
  await preferencesStorage.remove(key);
  broadcastPreferenceChange(key, undefined);
};

// One-time migration from a legacy localStorage key into IndexedDB.
// @cleanup Remove this function and call sites ~8 weeks after the IndexedDB migration ships.
// Call sites: useLocalStorage.ts, userPreferences.ts, useColorPalette.ts, useJobsColorPalette.ts.
export const migrateLegacyLocalStorageKey = async (
  key: string
): Promise<unknown | undefined> => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const existing = await getPreference(key);
    if (existing !== undefined) {
      return existing;
    }

    const raw = window.localStorage.getItem(key);
    if (raw === null) {
      return undefined;
    }

    const parsed = parseLegacyPreference(raw);
    await setPreference(key, parsed);
    window.localStorage.removeItem(key);
    return parsed;
  } catch (error) {
    Logger.debug(`Legacy preference migration failed for key "${key}":`, error);
    return undefined;
  }
};

const migrateLegacyBlobToPerUser = async (
  legacyKey: string,
  perUserKey: string,
  value: unknown
): Promise<unknown> => {
  await setPreference(perUserKey, value);
  window.localStorage.removeItem(legacyKey);
  return value;
};

// Extract one user's slice from a legacy multi-user localStorage blob and persist under a per-user key.
// @cleanup Remove this function and call sites ~8 weeks after the IndexedDB migration ships.
// Call sites: useLocalStorageByUser.ts, userPreferences.ts, runUserPreferenceMigrations.
export const migrateLegacyMultiUserPreference = async (
  legacyKey: string,
  perUserKey: string,
  userId: number
): Promise<unknown | undefined> => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const existing = await getPreference(perUserKey);
  if (existing !== undefined) {
    return existing;
  }

  try {
    const raw = window.localStorage.getItem(legacyKey);
    if (raw === null) {
      return undefined;
    }

    const parsed = parseLegacyPreference(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return migrateLegacyBlobToPerUser(legacyKey, perUserKey, parsed);
    }
    const parsedRecord = parsed as Record<string, unknown>;

    const userKey = String(userId);
    if (userKey in parsedRecord || userId in parsedRecord) {
      const userValue = parsedRecord[userKey] ?? (parsedRecord as Record<number, unknown>)[userId];
      await setPreference(perUserKey, userValue);
      delete parsedRecord[userKey];
      delete (parsedRecord as Record<number, unknown>)[userId];
      const remainingIds = Object.keys(parsedRecord);
      if (remainingIds.length === 0) {
        window.localStorage.removeItem(legacyKey);
      } else {
        window.localStorage.setItem(legacyKey, JSON.stringify(parsedRecord));
      }
      return userValue;
    }

    // Cap-table prefs were sometimes stored as a flat row map or per-cap-table map at the legacy root key.
    if (legacyKey === CAP_TABLE_ROW_GROUPS_PREFERENCES_KEY) {
      if (isFlatCapTableRowGroupPrefs(parsedRecord) || isNestedCapTableRowGroupPrefs(parsedRecord)) {
        return await migrateLegacyBlobToPerUser(legacyKey, perUserKey, parsedRecord);
      }
    }

    // Other users' slices still in the blob — do not claim the whole key for the current user.
    const hasOtherUserSlices = Object.keys(parsedRecord).some(
      key => isNumericKey(key) && key !== userKey
    );
    if (!hasOtherUserSlices && Object.keys(parsedRecord).length > 0) {
      return await migrateLegacyBlobToPerUser(legacyKey, perUserKey, parsedRecord);
    }
  } catch (error) {
    Logger.debug(`Legacy multi-user preference migration failed for key "${legacyKey}":`, error);
  }

  return migrateLegacyLocalStorageKey(perUserKey);
};

// Run legacy localStorage migrations for all per-user preference keys once per session.
// Cap-table prefs only migrated when visiting the tab; this ensures they move on any main-app load.
export const runUserPreferenceMigrations = async (userId: number): Promise<void> => {
  const keys = [
    PINNED_COMPANIES_KEY,
    PINNED_FUND_INVESTMENTS_KEY,
    PINNED_FUNDS_KEY,
    PINNED_FIRMS_KEY,
    SIDEBAR_ACTIVE_SECTION_KEY,
    CAP_TABLE_ROW_GROUPS_PREFERENCES_KEY,
  ];

  await Promise.all(
    keys.map(baseKey =>
      migrateLegacyMultiUserPreference(baseKey, preferenceKeyForUser(baseKey, userId), userId)
    )
  );
};

export const subscribePreferenceChanges = (
  onChange: (message: PreferenceChangeMessage) => void
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleCustom = (e: Event) => {
    const {detail} = (e as CustomEvent<{ key: string; value: unknown }>);
    if (detail?.key) {
      onChange({ key: detail.key, value: detail.value });
    }
  };

  window.addEventListener('localStorageChange', handleCustom as EventListener);

  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(PREFERENCES_BROADCAST_CHANNEL);
    channel.onmessage = (e: MessageEvent<PreferenceChangeMessage>) => {
      if (e.data?.key) {
        onChange(e.data);
      }
    };
  }

  return () => {
    window.removeEventListener('localStorageChange', handleCustom as EventListener);
    channel?.close();
  };
};
